// --- Core ---
import {each, find, findWhere, map} from "underscore";
// --- SaniQ ---
import {Address} from "./Address";
import {Cohort, deserializeCohort} from "./Cohort";
import {Contact} from "./Contact";
import {
  deserializeAssignments,
  DeserializedRecord,
  deserializeItem,
  deserializeManyRelationship,
  Deserializer,
  deserializeSingleRelationship,
  JSONAPIManyRelationship,
  JSONAPIRecord,
  JSONAPIReference,
  JSONAPISingleRelationship
} from "./Helpers";
import {Information} from "./Information";
import {Device} from "./patient/Device";
import {parseOptionalISO} from "helpers/Parser";
import {deserializePatientProgram, PatientProgram} from "./patient/PatientProgram";
import {Icd10Code} from "./icd10/code";
import {InfoBanner} from "./patient/InfoBanner";
import {deserializeMeasurementEcgTrigger, deserializeMeasurementTrigger, MeasurementEcgTrigger, MeasurementTrigger} from "./Trigger";
import {Role} from "./Role";
import {PatientConnectionMirth} from "./patient/connection/Mirth";
import {PatientConnectionMedisante} from "./patient/connection/Medisante";
import {deserializePatientSubscription, PatientSubscription} from "./patient/Subscription";

interface CohortAssignment extends DeserializedRecord<"patient_tag"> {
  patientId: string;
  tagId: string;
}

interface PatientCounts {
  findings: number;
  measurementTriggers: number;
  studies: number;
  surveys: number;
  surveyTriggers: number;
  tasks: number;
  triggers: number;
  videoConsultations: number;
  devices: number;
}

interface Physician extends JSONAPIReference<"physician"> {
  primary: boolean;
}

interface TmhiAdmissionConfiguration extends DeserializedRecord<"patient_tmhi_admission_configuration"> {
  exemptYearlyReport: boolean;
  tenantIsPrimaryTreatingPhysician: boolean;
  hasExternalDevices: boolean,
  hasIntensiveMonitoring: boolean,
  admissionCountBeforeTelemonitoring?: number
  inpatientStayCountByNotices: number,
  hasImplant: boolean,
  implantManufacturer: string
}

interface TmhiAdmission extends DeserializedRecord<"tmhi_admission"> {
  // Number of clinical admissions in the year before starting the telemonitoring
  inpatientStayCount: number;
  suitableForTmhi: boolean;
  // Define inclusion of the patient in the yearly report. Used when monitoring patients outside of the QSV
  // New York Heart Association classification
  nyhaClassification?: number;
  // Left ventricular ejection fraction. Used for calculation of ESC classification
  lvef?: number;
  // Right ventricular ejection fraction
  rvef?: number;
  hasImplant: boolean;
  implantManufacturer?: string;
  review?: string;
  reviewDateAt?: Date;
}

interface VitalDatum extends DeserializedRecord<"vital_datum"> {
  birthDate: Date;
  diedOn?: Date;
  hasDied?: boolean;
  isUnableToWork?: boolean;
  unableToWorkUntilDate?: Date;
  childrenBirthday: {birthday: Date}[];
  gender: 'male' | 'female' | 'diverse' | 'unknown';
  height?: number;
  jobTitle?: string;
  longTermCareLevel?: number;
  maritalStatus: 'divorced' | 'married' | 'single' | 'widowed';
  pension: boolean;
  weight?: number;
}

interface PhysicianAssignment extends DeserializedRecord<"physician_patient"> {
  patientId: string;
  physicianId: string;
  primary: boolean;
}

interface RoleAssignment extends DeserializedRecord<"role_patient"> {
  patientId: string;
  roleId: string;
}

type PatientConnectionSubject = PatientConnectionMirth | PatientConnectionMedisante;

interface PatientConnection extends DeserializedRecord<"connection"> {
  lastConnectionAt?: Date;
  subject: PatientConnectionSubject;
}

interface PatientInsurance extends DeserializedRecord<"insurance"> {
  insuranceLevel: "statutory" | "privately" | "other";
}

interface Patient extends DeserializedRecord<"patient"> {
  activityLevel: number;
  address: Address;
  aisId?: string;
  aisSynchronizedAt?: Date;
  cohortAssignments: CohortAssignment[];
  cohorts: Cohort[];
  connections: PatientConnection[];
  contacts: Contact[];
  customPatientId: string;
  deletedAt?: Date;
  devices: Device[];
  icd10s: Icd10Code[];
  inactive: boolean;
  inactiveMessage?: string;
  inactivatedAt?: Date;
  paused: boolean;
  pausePlanned: boolean;
  pausedAt?: Date;
  pausedEndAt?: Date;
  pauseMessage?: string;
  information: Information;
  insurance: PatientInsurance;
  physicianAssignments: PhysicianAssignment[];
  physicians: Physician[];
  patientPrograms: PatientProgram[];
  activeProgram?: PatientProgram;
  roleAssignments: RoleAssignment[];
  roles: Role[];
  /*
   * @deprecated use cohortAssignments instead
   */
  tagAssignments?: DeserializedRecord[];
  /*
   * @deprecated use cohorts instead
   */
  tags?: DeserializedRecord[];
  tmhiAdmissionConfiguration?: TmhiAdmissionConfiguration;
  tmhiAdmissions: TmhiAdmission[];
  vitalDatum: VitalDatum;
  patientInfoBanner?: InfoBanner;
  scopedInfoBanners?: InfoBanner[];
  patientMeasurementTriggers: MeasurementTrigger[];
  patientMeasurementEcgTriggers: MeasurementEcgTrigger[];
  patientSubscriptions: PatientSubscription[];
  isSpecialMonitored: boolean;
  hasExternalCarer: boolean;
}

interface PatientData extends JSONAPIRecord {
  relationships: {
    address: JSONAPISingleRelationship;
    connections: JSONAPIManyRelationship;
    contacts: JSONAPIManyRelationship;
    icd10s: JSONAPIManyRelationship;
    information: JSONAPISingleRelationship;
    insurance: JSONAPISingleRelationship;
    patient_devices: JSONAPIManyRelationship;
    patient_tags: JSONAPIManyRelationship;
    patient_vital_datum: JSONAPISingleRelationship;
    physician_patients: JSONAPIManyRelationship;
    physicians: JSONAPIManyRelationship;
    role_patients: JSONAPIManyRelationship;
    roles: JSONAPIManyRelationship;
    tags: JSONAPIManyRelationship;
    tmhi_admissions: JSONAPIManyRelationship;
    patient_tmhi_admission_configuration: JSONAPISingleRelationship;
    patient_programs: JSONAPIManyRelationship;
    patient_info_banner: JSONAPISingleRelationship;
    patient_measurement_triggers: JSONAPIManyRelationship;
    patient_measurement_ecg_triggers: JSONAPIManyRelationship;
    patient_subscriptions: JSONAPIManyRelationship;
    scoped_info_banners: JSONAPIManyRelationship;
  } & JSONAPIRecord["relationships"];
}

interface VitalDatumData extends JSONAPIRecord {
  attributes: {
    children_birthday: string[];
  } & JSONAPIRecord["attributes"];
}

interface PatientConnectionData extends JSONAPIRecord {
  relationships: {
    subject: JSONAPISingleRelationship
  }
}

const deserializeConnection: Deserializer<PatientConnection, PatientConnectionData> = (data, included) => {
  const subject = deserializeSingleRelationship<PatientConnectionSubject>(data.relationships.subject, included);

  return {
    ...deserializeItem(data),
    subject: subject
  };
};

const deserializeVitalDatum: Deserializer<VitalDatum, VitalDatumData> = (data) => ({
  ...deserializeItem(data),
  birthDate: parseOptionalISO(data.attributes.birth_date),
  diedOn: parseOptionalISO(data.attributes.died_on),
  childrenBirthday: map(data.attributes.children_birthday, (bday) => ({birthday: parseOptionalISO(bday)}))
});

const deserializePatient: Deserializer<Patient, PatientData> = (data, included) => {
  const address = deserializeSingleRelationship<Address>(data.relationships.address, included);
  const cohorts = deserializeManyRelationship(data.relationships.tags, included, deserializeCohort);
  const connections = deserializeManyRelationship(data.relationships.connections, included, deserializeConnection);
  const contacts = deserializeManyRelationship<Contact>(data.relationships.contacts, included);
  // eslint-disable-next-line @stylistic/max-len
  const cohortAssignments = deserializeManyRelationship(data.relationships.patient_tags, included, deserializeAssignments(['patient', 'tag'])) as CohortAssignment[];
  const devices = deserializeManyRelationship<Device>(data.relationships.patient_devices, included);
  const icd10s = deserializeManyRelationship<Icd10Code>(data.relationships.icd10s, included);
  const information = deserializeSingleRelationship<Information>(data.relationships.information, included);
  const insurance = deserializeSingleRelationship<PatientInsurance>(data.relationships.insurance, included);
  const physicians = deserializeManyRelationship<Physician>(data.relationships.physicians, included);
  // eslint-disable-next-line @stylistic/max-len
  const physicianAssignments = deserializeManyRelationship(data.relationships.physician_patients, included, deserializeAssignments(['patient', 'physician'])) as PhysicianAssignment[];
  const roles = deserializeManyRelationship<Role>(data.relationships.roles, included);
  // eslint-disable-next-line @stylistic/max-len
  const roleAssignments = deserializeManyRelationship(data.relationships.role_patients, included, deserializeAssignments(['patient', 'role'])) as RoleAssignment[];
  const tmhiAdmissions = deserializeManyRelationship<TmhiAdmission>(data.relationships.tmhi_admissions, included);

  const tmhiAdmissionConfiguration = deserializeSingleRelationship<TmhiAdmissionConfiguration>(
    data.relationships.patient_tmhi_admission_configuration,
    included
  );

  const vitalDatum = deserializeSingleRelationship<VitalDatum>(
    data.relationships.patient_vital_datum, included,
    deserializeVitalDatum
  );

  const patientPrograms = deserializeManyRelationship<PatientProgram>(data.relationships.patient_programs, included, deserializePatientProgram);
  const activeProgram = findWhere(patientPrograms, {isCurrent: true});
  const patientInfoBanner = deserializeSingleRelationship<InfoBanner>(data.relationships.patient_info_banner, included);
  const scopedInfoBanners = deserializeManyRelationship<InfoBanner>(data.relationships.scoped_info_banners, included);
  // eslint-disable-next-line @stylistic/max-len
  const patientMeasurementTriggers = deserializeManyRelationship<MeasurementTrigger>(data.relationships.patient_measurement_triggers, included, deserializeMeasurementTrigger);
  // eslint-disable-next-line @stylistic/max-len
  const patientMeasurementEcgTriggers = deserializeManyRelationship<MeasurementEcgTrigger>(data.relationships.patient_measurement_ecg_triggers, included, deserializeMeasurementEcgTrigger);

  each(physicians, (physician) => {
    physician.primary = find(physicianAssignments, ({physicianId}) => String(physicianId) === String(physician.id))?.primary || false;
  });

  const patientSubscriptions = deserializeManyRelationship(data.relationships.patient_subscriptions, included, deserializePatientSubscription);

  return {
    ...deserializeItem(data),
    address,
    cohorts,
    connections,
    contacts,
    devices,
    patientInfoBanner,
    tags: cohorts,
    cohortAssignments,
    tagAssignments: cohortAssignments,
    icd10s,
    information,
    physicians,
    physicianAssignments,
    patientPrograms,
    activeProgram,
    roles,
    roleAssignments,
    tmhiAdmissions,
    tmhiAdmissionConfiguration,
    vitalDatum,
    insurance,
    patientMeasurementTriggers,
    patientMeasurementEcgTriggers,
    patientSubscriptions,
    scopedInfoBanners
  };
};

export type {
  Patient,
  PatientData,
  PatientCounts,
  TmhiAdmission,
  TmhiAdmissionConfiguration,
  VitalDatum
};

export {
  deserializePatient,
  deserializeVitalDatum
};
